/**
 * /dm/:threadId — DM thread view.
 *
 * - Loads thread metadata + initial message page on mount
 * - Opens a WebSocket scoped to this thread (the WS hub broadcasts
 *   all events; we filter by thread_id locally + ignore others)
 * - Renders bubbles with sender-aware alignment (your messages right,
 *   theirs left), read receipts on your sends, typing dots when the
 *   other party is typing
 * - Auto-marks-read on viewport — when an inbound message becomes
 *   visible, fires markRead with that id as the cutoff
 * - Reveal modal: shown the first time you (the initiator) try to
 *   send a message in a brand-new thread. Confirms you understand
 *   the other side will see your name + profile.
 * - Block + report from the header menu
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  api,
  APIError,
  dmApi,
  type DMMessage,
  type DMThread,
  type User,
} from '@/lib/api';
import { Icons } from '@/components';
import { openDMSocket, type DMSocket } from '@/lib/dmws';
import { useAppContext, BADGE_REFRESH_EVENT } from '@/components/AppShell';
import { Navigate } from 'react-router-dom';
import { friendlyError } from '@/lib/errors';

export function DMThreadPage() {
  const { user } = useAppContext();
  const { threadId = '' } = useParams<{ threadId: string }>();

  // Premium gate — keep free users out of the thread view even when
  // they hit /dm/<id> directly. The inbox screen at /dm shows them
  // the paywall + count of waiting messages.
  if (user.tier !== 'premium') {
    return <Navigate to="/dm" replace />;
  }

  const [me, setMe] = useState<User | null>(null);
  const [thread, setThread] = useState<DMThread | null>(null);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [otherTyping, setOtherTyping] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState<string | null>(null); // message_id being reported
  const [err, setErr] = useState<string | null>(null);
  const wsRef = useRef<DMSocket | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const otherTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Initial load. Auth gate lives in AppShell.
  useEffect(() => {
    (async () => {
      try {
        const [u, t, m] = await Promise.all([
          api.me(),
          dmApi.getThread(threadId),
          dmApi.listMessages(threadId),
        ]);
        setMe(u);
        setThread(t);
        // Backend returns messages newest-first; reverse for natural
        // top-to-bottom oldest-to-newest rendering.
        setMessages([...m.messages].reverse());
      } catch (e) {
        if (e instanceof APIError && e.status === 404) {
          setErr('Thread not found.');
        } else {
          setErr(friendlyError(e, 'DMThread'));
        }
      }
    })();
  }, [threadId]);

  // WS subscription.
  useEffect(() => {
    if (!me || !thread) return;
    const sock = openDMSocket({
      onMessage: (e) => {
        if (e.thread_id !== threadId) return;
        setMessages((prev) => {
          // Dedupe — our own sends arrive via WS echo AND the HTTP
          // POST response. Either is fine; both is a duplicate render.
          if (prev.some((m) => m.id === e.message.id)) return prev;
          return [...prev, e.message];
        });
      },
      onTyping: (e) => {
        if (e.thread_id !== threadId || e.by_user_id === me.id) return;
        setOtherTyping(e.type === 'typing.start');
        if (otherTypingTimer.current) clearTimeout(otherTypingTimer.current);
        // Auto-clear after 6s in case we miss the stop event (mobile
        // backgrounding, network blip, etc).
        if (e.type === 'typing.start') {
          otherTypingTimer.current = setTimeout(() => setOtherTyping(false), 6000);
        }
      },
      onRead: (e) => {
        if (e.thread_id !== threadId) return;
        // Their message.read event tells us they read up to a given
        // message of OURS. Mark our messages ≤ that id as read.
        setMessages((prev) =>
          prev.map((m) =>
            m.sender_id === me.id && m.read_at == null && m.id <= e.up_to_message_id
              ? { ...m, read_at: e.at }
              : m,
          ),
        );
      },
      onBlocked: (e) => {
        if (e.thread_id !== threadId) return;
        setThread((t) => t && ({ ...t, blocked_by_user_id: e.by_user_id }));
      },
    });
    wsRef.current = sock;
    return () => sock.close();
  }, [me, thread, threadId]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, otherTyping]);

  // Mark-read on render of any newer-than-me inbound message.
  useEffect(() => {
    if (!me || messages.length === 0) return;
    const lastInbound = [...messages].reverse().find((m) => m.sender_id !== me.id && m.read_at == null);
    if (!lastInbound) return;
    dmApi.markRead(threadId, lastInbound.id)
      .then(() => {
        // Nudge AppShell to re-poll its unread breakdown immediately
        // so the Messages-tab badge clears the moment the user opens
        // a thread, instead of waiting up to 30s for the next tick.
        window.dispatchEvent(new Event(BADGE_REFRESH_EVENT));
      })
      .catch(() => null);
    // Locally mark them read (optimistic).
    setMessages((prev) =>
      prev.map((m) =>
        m.sender_id !== me.id && m.read_at == null
          ? { ...m, read_at: new Date().toISOString() }
          : m,
      ),
    );
  }, [messages, me, threadId]);

  const blocked = thread?.blocked_by_user_id != null;
  const isInitiator = me && thread && thread.initiator_user_id === me.id;
  const hasMessages = messages.length > 0;
  const needsReveal = isInitiator && !hasMessages;

  const onSend = useCallback(async () => {
    const body = draft.trim();
    if (!body || !thread || !me) return;
    if (needsReveal && !reveal) {
      setReveal(true);
      return;
    }
    setReveal(false);
    setDraft('');
    setErr(null);
    try {
      const msg = await dmApi.send(thread.id, body);
      setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]);
    } catch (e) {
      setErr(friendlyError(e, 'DMThread'));
      setDraft(body); // restore for retry
    }
  }, [draft, thread, me, needsReveal, reveal]);

  const onTypingChange = useCallback((next: string) => {
    setDraft(next);
    if (!wsRef.current || !thread) return;
    wsRef.current.sendTyping(thread.id, next.length > 0);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    if (next.length > 0) {
      typingTimer.current = setTimeout(() => {
        wsRef.current?.sendTyping(thread.id, false);
      }, 3000);
    }
  }, [thread]);

  const onBlock = useCallback(async () => {
    if (!thread) return;
    if (!confirm('Block this conversation? Neither side will be able to message the other.')) return;
    try {
      await dmApi.block(thread.id);
      setThread({ ...thread, blocked_by_user_id: me?.id });
      setMenuOpen(false);
    } catch (e) {
      setErr(friendlyError(e, 'DMThread'));
    }
  }, [thread, me]);

  const onReport = useCallback(async (messageId: string, reason: string) => {
    try {
      await dmApi.report(messageId, reason);
      setReportOpen(null);
    } catch (e) {
      setErr(friendlyError(e, 'DMThread'));
    }
  }, []);

  if (err && !thread) {
    return (
      <main style={pageEmpty}>
        <Link to="/dm" style={backLink}>← Back to DMs</Link>
        <div style={errorBox}>{err}</div>
      </main>
    );
  }

  if (!thread || !me) {
    return (
      <main style={pageEmpty}>
        <Link to="/dm" style={backLink}>← Back</Link>
        <p style={dim}>Loading…</p>
      </main>
    );
  }

  const otherDisplay = thread.other_user.display_name || 'Someone';
  const otherSlug = thread.other_user.slug;
  const otherAvatar = thread.other_user.avatar_url;
  const headerInfo = (
    <>
      <Avatar src={otherAvatar} alt={otherDisplay} name={otherDisplay} size={36} />
      <span style={{ fontWeight: 600 }}>{otherDisplay}</span>
    </>
  );

  return (
    <main className="dm-thread-shell" style={page}>
      {/* Header — avatar + (clickable) recipient name + menu. Tapping the
          name/avatar opens the recipient's public profile; private
          profiles are bypassed for DM partners server-side. */}
      <div style={header}>
        <Link to="/dm" style={backLink}>←</Link>
        {otherSlug ? (
          <Link to={`/p/${otherSlug}`} style={headerNameLink} title="View profile">
            {headerInfo}
          </Link>
        ) : (
          <div style={headerNameLink}>{headerInfo}</div>
        )}
        <button onClick={() => setMenuOpen((v) => !v)} style={menuBtn} aria-label="Thread menu">⋯</button>
        {menuOpen && (
          <div style={menuPanel} onMouseLeave={() => setMenuOpen(false)}>
            <button onClick={onBlock} style={menuItem}>Block</button>
          </div>
        )}
      </div>

      {/* Blocked banner */}
      {blocked && (
        <div style={blockedBanner}>
          This conversation is closed. {thread.blocked_by_user_id === me.id
            ? "You blocked it."
            : "The other person blocked it."}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollerRef} className="dm-scroller" style={scroller}>
        {messages.map((m) => (
          <Bubble
            key={m.id}
            msg={m}
            mine={m.sender_id === me.id}
            onReport={() => setReportOpen(m.id)}
          />
        ))}
        {otherTyping && <TypingDots />}
      </div>

      {/* Input */}
      {!blocked && (
        <form
          className="dm-input-form"
          onSubmit={(e) => { e.preventDefault(); void onSend(); }}
          style={inputForm}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => onTypingChange(e.target.value)}
            placeholder="Write something…"
            maxLength={2000}
            style={input}
          />
          <button
            type="submit"
            aria-label="Send message"
            disabled={draft.trim().length === 0}
            style={{ ...sendBtn, opacity: draft.trim().length === 0 ? 0.5 : 1 }}
          >
            <Icons.send size={18} />
          </button>
        </form>
      )}

      {/* Reveal modal — `.dm-reveal-modal` + `.dm-reveal-actions` are
          mobile-responsive hooks (base.css collapses to column-reverse
          stacked buttons + tighter padding on ≤900px). */}
      {reveal && (
        <Modal onClose={() => setReveal(false)} className="dm-reveal-modal">
          <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            Heads up
          </div>
          <h2 className="serif" style={{ fontSize: 26, fontStyle: 'italic', fontWeight: 500, marginBottom: 10 }}>
            You're about to reveal yourself to {thread.other_user.display_name || 'this person'}.
          </h2>
          <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 24 }}>
            They'll see your display name and your profile. Their feedback
            you've already gotten stays anonymous; this just opens a direct
            line.
          </p>
          <div className="dm-reveal-actions" style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setReveal(false)} style={ghostBtn}>Not yet</button>
            <button onClick={onSend} style={primaryBtn}>Send the message</button>
          </div>
        </Modal>
      )}

      {/* Report modal */}
      {reportOpen && (
        <ReportModal
          messageId={reportOpen}
          onClose={() => setReportOpen(null)}
          onSubmit={onReport}
        />
      )}

      {err && <div style={errorBoxFloat}>{err}</div>}
    </main>
  );
}

// ── small components ──────────────────────────────────────────────────

function Bubble({ msg, mine, onReport }: { msg: DMMessage; mine: boolean; onReport: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
      <div style={{ maxWidth: '78%' }}>
        <div style={{
          padding: '10px 14px', borderRadius: 18,
          background: mine ? 'var(--coral)' : 'var(--surface)',
          color: mine ? '#fff' : 'var(--ink)',
          fontSize: 15, lineHeight: 1.4,
          borderBottomRightRadius: mine ? 6 : 18,
          borderBottomLeftRadius:  mine ? 18 : 6,
          whiteSpace: 'pre-wrap',
        }}>
          {msg.body}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--ink-3)', marginTop: 4,
          textAlign: mine ? 'right' : 'left',
          display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start',
          gap: 8, alignItems: 'center',
        }}>
          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {mine && msg.read_at && <span style={{ color: 'var(--coral)' }}>✓✓ read</span>}
          {mine && !msg.read_at && <span>delivered</span>}
          {!mine && (
            <button onClick={onReport} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--ink-3)', padding: 0, fontSize: 11,
            }}>report</button>
          )}
        </div>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px' }}>
      <Dot delay={0} />
      <Dot delay={120} />
      <Dot delay={240} />
      <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 6 }}>typing…</span>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span style={{
      width: 6, height: 6, borderRadius: 6,
      background: 'var(--ink-3)',
      display: 'inline-block',
      animation: `dmtypingBounce 1.2s ease-in-out ${delay}ms infinite`,
    }} />
  );
}

// Avatar — circular lead-photo with initials fallback. Used in the
// thread header so the recipient is visually identifiable at a glance
// (especially when display names are similar across threads). Falls
// back to the first letter of the name on a chip when no photo.
function Avatar({ src, alt, name, size }: { src?: string; alt?: string; name: string; size: number }) {
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: size, height: size, borderRadius: size,
        background: 'var(--surface-2)',
        overflow: 'hidden', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {src
        ? <img src={src} alt={alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ fontFamily: 'serif', fontStyle: 'italic', fontSize: size * 0.45, color: 'var(--ink-2)' }}>{initial}</span>}
    </div>
  );
}

function Modal({ children, onClose, className }: { children: React.ReactNode; onClose: () => void; className?: string }) {
  return (
    <div style={scrim} onClick={onClose}>
      <div className={className} style={modal} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ReportModal({
  messageId, onClose, onSubmit,
}: { messageId: string; onClose: () => void; onSubmit: (id: string, reason: string) => void }) {
  const [reason, setReason] = useState('');
  const reasons = useMemo(() => [
    'Harassment or threats',
    'Sexual content',
    'Spam or solicitation',
    'Impersonation',
    'Other (specify)',
  ], []);
  const [custom, setCustom] = useState('');
  const finalReason = reason === 'Other (specify)' ? custom.trim() : reason;
  const canSubmit = finalReason.length > 0;
  return (
    <Modal onClose={onClose}>
      <h2 className="serif" style={{ fontSize: 22, fontStyle: 'italic', fontWeight: 500, marginBottom: 12 }}>
        Report message
      </h2>
      <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 14 }}>
        Tell us what's wrong with this message. A moderator reviews
        reports manually.
      </p>
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={selectStyle}
      >
        <option value="">Pick a reason</option>
        {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {reason === 'Other (specify)' && (
        <textarea
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Describe briefly…"
          maxLength={500}
          rows={3}
          style={textareaStyle}
        />
      )}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button
          disabled={!canSubmit}
          onClick={() => onSubmit(messageId, finalReason)}
          style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.5 }}
        >
          Submit report
        </button>
      </div>
    </Modal>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  maxWidth: 640, margin: '0 auto',
  display: 'flex', flexDirection: 'column',
  height: '100vh', padding: '0 16px',
};
const pageEmpty: React.CSSProperties = { maxWidth: 640, margin: '0 auto', padding: '40px 16px' };
const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0',
  borderBottom: '1px solid var(--line)', position: 'relative',
};
const backLink: React.CSSProperties = { color: 'var(--ink-3)', fontSize: 18, textDecoration: 'none' };
// headerNameLink wraps the avatar + recipient name in a flex row that
// behaves like an interactive link without changing color.
const headerNameLink: React.CSSProperties = {
  flex: 1,
  fontSize: 15,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  textDecoration: 'none',
  color: 'inherit',
  minWidth: 0,
};
const menuBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 22, color: 'var(--ink-3)', padding: '0 8px',
};
const menuPanel: React.CSSProperties = {
  position: 'absolute', top: '100%', right: 0,
  background: 'var(--surface)', borderRadius: 12, padding: 8,
  border: '1px solid var(--line)', boxShadow: 'var(--shadow-card)',
  zIndex: 10, minWidth: 200,
};
const menuItem: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 14, color: '#a8331f', padding: '8px 12px', width: '100%',
  textAlign: 'left',
};

const scroller: React.CSSProperties = {
  flex: 1, overflowY: 'auto',
  padding: '16px 0',
};

const inputForm: React.CSSProperties = {
  display: 'flex', gap: 8, padding: '12px 0 24px 0',
  borderTop: '1px solid var(--line)',
};
const input: React.CSSProperties = {
  flex: 1, padding: '10px 14px', fontSize: 15,
  borderRadius: 100, border: '1px solid var(--line-strong)',
  fontFamily: 'inherit', background: 'var(--bg)',
};
// Square-ish circular button matching the input row height — the
// paper-plane icon doesn't need text padding.
const sendBtn: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff', border: 'none',
  borderRadius: 100,
  width: 40, height: 40,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

const dim: React.CSSProperties = { color: 'var(--ink-3)', padding: 32, textAlign: 'center' };
const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)', color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)', borderRadius: 12,
  padding: '10px 14px', fontSize: 13, margin: '16px 0',
};
const errorBoxFloat: React.CSSProperties = {
  ...errorBox, position: 'fixed', bottom: 80, left: 16, right: 16,
  maxWidth: 600, margin: '0 auto', zIndex: 5,
};
const blockedBanner: React.CSSProperties = {
  background: 'rgba(43,29,20,0.05)', color: 'var(--ink-2)',
  border: '1px solid var(--line)', borderRadius: 12,
  padding: '10px 14px', fontSize: 13, margin: '8px 0',
};

const scrim: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20, zIndex: 50,
};
const modal: React.CSSProperties = {
  width: '100%', maxWidth: 440,
  background: 'var(--surface)', borderRadius: 22, padding: 28,
};
const primaryBtn: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff', border: 'none',
  borderRadius: 100, padding: '10px 22px', fontWeight: 600, fontSize: 14,
  cursor: 'pointer', flex: 1,
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent', color: 'var(--ink)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100, padding: '10px 22px', fontWeight: 600, fontSize: 14,
  cursor: 'pointer', flex: 1,
};
const selectStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 14,
  background: 'var(--bg)', border: '1px solid var(--line)',
  borderRadius: 8, marginBottom: 14, fontFamily: 'inherit',
};
const textareaStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 14,
  background: 'var(--bg)', border: '1px solid var(--line)',
  borderRadius: 8, marginBottom: 14, fontFamily: 'inherit', resize: 'vertical',
};
